// src/app/api/authApi.ts
import { baseApi } from "./baseApi";

/* ─────────────────────────────────────────────────────────────────
   CHILD TYPE  (matches ChildModel.toResponse())
───────────────────────────────────────────────────────────────── */

export type StudentTrack =
  | "foundation_builders"  // ages 6–9
  | "explorers"            // ages 9–12
  | "innovators"           // ages 13–18
  | "unassigned";

export type StudentStatus =
  | "prospect"   // default — not yet paid
  | "enrolled"   // payment confirmed
  | "active"     // currently attending sessions
  | "inactive";  // no longer attending

export interface Child {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  dateOfBirth: string;        // ISO date string
  age: number;
  track: StudentTrack;
  studentStatus: StudentStatus;
  role: "student";
  parent: string | null;      // parent User ID, null if self-registered
  userAccount: string | null; // User ID if child has login credentials
  avatar: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ─────────────────────────────────────────────────────────────────
   USER TYPE  (matches UserModel + formatUserResponse())
───────────────────────────────────────────────────────────────── */

/**
 * "learner" is a child (9+) signed in with the username + password their
 * parent set up. The session belongs to the child and opens only Learn.
 */
export type PublicRole = "student" | "parent" | "tutor" | "user" | "learner";

export interface AuthUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar: string | null;
  role: PublicRole;
  /** Only present when role === "parent" */
  children?: Child[];
  /** Only present when role === "learner": the child signed in */
  childId?: string;
  track?: StudentTrack;
  isEmailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ─────────────────────────────────────────────────────────────────
   REQUEST TYPES
───────────────────────────────────────────────────────────────── */

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LearnerLoginRequest {
  username: string;
  password: string;
}

/** Sent per child inside a parent registration */
export interface ChildRegistrationInput {
  firstName: string;
  lastName: string;
  dateOfBirth: string;  // "YYYY-MM-DD"
  email?: string;       // optional — many young children have none
}

export interface ParentRegisterRequest {
  role: "parent";
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  children: ChildRegistrationInput[];   // at least 1 required
}

export interface StudentRegisterRequest {
  role: "student";
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  dateOfBirth: string;
}

/** Union — the backend accepts both shapes on the same POST /register route */
export type RegisterRequest = ParentRegisterRequest | StudentRegisterRequest;

export interface AddChildRequest {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email?: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  avatar?: string;
}

/* ─────────────────────────────────────────────────────────────────
   RESPONSE TYPES
───────────────────────────────────────────────────────────────── */

export interface AuthResponse {
  success: boolean;
  message: string;
  user: AuthUser;
  token: string;
}

interface MessageResponse {
  success: boolean;
  message: string;
  data: null;
}

interface UserResponse {
  success: boolean;
  message: string;
  data: AuthUser;
}

interface AddChildResponse {
  success: boolean;
  message: string;
  data: {
    child: Child;
    user: AuthUser;
  };
}

/* ─────────────────────────────────────────────────────────────────
   API SLICE
───────────────────────────────────────────────────────────────── */

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Unified login — rejects admin-portal roles server-side */
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: "/auth/login",
        method: "POST",
        body: credentials,
      }),
    }),

    /** A child signs in with their learner login (username, no email) */
    learnerLogin: builder.mutation<AuthResponse, LearnerLoginRequest>({
      query: (credentials) => ({
        url: "/auth/learner-login",
        method: "POST",
        body: credentials,
      }),
    }),

    /**
     * Unified register — accepts both parent and student payloads.
     * The `role` field in the body determines the backend flow.
     */
    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (data) => ({
        url: "/auth/register",
        method: "POST",
        body: data,
      }),
    }),

    /** Logout (stateless — client clears token) */
    logout: builder.mutation<MessageResponse, void>({
      query: () => ({ url: "/auth/logout", method: "POST" }),
    }),

    /** Get the currently authenticated user's profile */
    getCurrentUser: builder.query<UserResponse, void>({
      query: () => "/auth/me",
      providesTags: [{ type: "User", id: "CURRENT" }],
    }),

    /** Update name / avatar */
    updateProfile: builder.mutation<UserResponse, UpdateProfileRequest>({
      query: (data) => ({
        url: "/auth/profile",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: [{ type: "User", id: "CURRENT" }],
    }),

    /** Authenticated parent adds an additional child */
    addChild: builder.mutation<AddChildResponse, AddChildRequest>({
      query: (data) => ({
        url: "/auth/add-child",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "User", id: "CURRENT" }],
    }),

    changePassword: builder.mutation<
      MessageResponse,
      { currentPassword: string; newPassword: string }
    >({
      query: (data) => ({
        url: "/auth/change-password",
        method: "POST",
        body: data,
      }),
    }),

    forgotPassword: builder.mutation<MessageResponse, { email: string }>({
      query: (data) => ({
        url: "/auth/forgot-password",
        method: "POST",
        body: data,
      }),
    }),

    resetPassword: builder.mutation<
      MessageResponse,
      { token: string; password: string }
    >({
      query: (data) => ({
        url: "/auth/reset-password",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useLearnerLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useUpdateProfileMutation,
  useAddChildMutation,
  useChangePasswordMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
} = authApi;